/**
 * Copyright (C) Siemens AG 1996-2024 ALL RIGHTS RESERVED.
 */

import {
  AfterViewInit,
  Component,
  inject,
  Input,
  OnDestroy,
  OnInit,
} from '@angular/core';

import {FormFieldAttribute} from '../../../../../../models/FormCategory';
import {GeneralDataHandlingService} from '../../../../../../services/general.data-handling.service';
import {NgxEchartsDirective} from 'ngx-echarts';
import {EChartsOption} from 'echarts';
import {registerTheme} from '@siemens/ix-echarts';
import * as echarts from 'echarts/core';
import {AppSettings} from '../../../../../../models/Constants';
import {PassportDto} from '../../../../../../models/Passport';
import {TranslateService} from '@ngx-translate/core';
import {OptionDataValueNumeric} from 'echarts/types/src/util/types';
import {ThemeService} from '../../../../../../services/theme.service';
import {PassportTranslationService} from '../../../../../../services/passport-translation.service';
import {Subscription} from 'rxjs';

@Component({
  selector: 'app-pie',
  imports: [NgxEchartsDirective],
  templateUrl: './pie.component.html',
  styleUrl: './pie.component.scss',
})
export class PieComponent implements OnInit, AfterViewInit, OnDestroy {
  private readonly dataHandler = inject(GeneralDataHandlingService);
  private readonly translate = inject(TranslateService);
  private readonly themeService = inject(ThemeService);
  private readonly passportTranslation = inject(PassportTranslationService);

  @Input({required: true}) attribute!: FormFieldAttribute;
  @Input({required: true}) passport!: PassportDto['data'];
  @Input({required: true}) schemaId!: string;

  options: EChartsOption = {};

  private changeLangSubscription?: Subscription;

  ngAfterViewInit() {
    const container = document.querySelector(
      `#line-chart-container-${this.attribute.name}`,
    ) as HTMLElement;
    if (container) {
      container.style.height = '400px';
    }
    setTimeout(() => {
      this.options = this.buildOptions();
    }, AppSettings.INPUT_TO_REQUEST_WAIT_TIME_IN_MS);
  }

  ngOnInit() {
    registerTheme(echarts);

    // Re-build the chart in the newly selected language once ngx-translate
    // has finished loading it. The language selection/loading itself is
    // already handled elsewhere; here we only react to the resulting event.
    this.changeLangSubscription = this.translate.onLangChange.subscribe(() => {
      this.options = this.buildOptions();
    });
  }

  ngOnDestroy(): void {
    this.changeLangSubscription?.unsubscribe();
  }

  getChartTheme() {
    return this.themeService.actualChartTheme;
  }

  buildOptions(): EChartsOption {
    const selectAttribute = (this.attribute.attributes ?? []).find(
      (e) => e.type === 'select',
    );
    const numberAttribute = (this.attribute.attributes ?? []).find(
      (e) => e.type === 'number',
    );
    const selectValuePath = selectAttribute?.valuePath ?? '';
    const selectOptions = selectAttribute?.options ?? [];
    const attributeUnit = this.getUnit(numberAttribute?.unit || '');
    const valueValuePath = numberAttribute?.valuePath || '';
    const data = this.dataHandler
      .getValueByPath(this.passport, this.attribute.valuePath)
      .reduce((acc: Record<string, number>, item: unknown) => {
        const label = this.dataHandler.getValueByPath(item, selectValuePath);
        const optionLabel = (selectOptions || []).find(
          (opt) => opt.value === label,
        )?.label;
        const translatedLabel = this.translateSelectValue(
          selectValuePath,
          String(label ?? ''),
          optionLabel,
        );
        const value = this.dataHandler.getValueByPath(item, valueValuePath);
        if (translatedLabel && value) {
          acc[translatedLabel] = (acc[translatedLabel] || 0) + value;
        }
        return acc;
      }, {});

    return {
      media: [
        {
          query: {maxWidth: 440},
          option: {
            series: {
              label: {show: false},
              radius: '90%',
              bottom: 60,
            },
          },
        },
      ],
      legend: {
        bottom: 'bottom',
      },
      series: {
        name: 'test',
        type: 'pie',
        data: Object.entries(data).map(([name, value]) => ({
          name,
          value: value as OptionDataValueNumeric,
        })),
        radius: '80%',
        label: {
          show: true,
          position: 'outside',
          formatter: `{c} ${attributeUnit}`,
        },
      },
    };
  }

  getUnit(unit: string): string {
    const translateValue = `general.units.${unit}`;
    const translated = this.translate.instant(translateValue);
    if (translated === translateValue) {
      return unit;
    } else {
      return translated;
    }
  }

  private translateSelectValue(
    valuePath: string,
    rawValue: string,
    staticFallbackLabel?: string,
  ): string {
    if (!rawValue) {
      return '';
    }

    const direct = this.passportTranslation.translateValueByPath(
      valuePath,
      this.schemaId,
      rawValue,
      rawValue,
    );
    if (direct !== rawValue) {
      return direct;
    }

    const contextual = this.passportTranslation.translateValueByPath(
      `${this.attribute.valuePath}.${valuePath}`,
      this.schemaId,
      rawValue,
      rawValue,
    );
    if (contextual !== rawValue) {
      return contextual;
    }

    if (staticFallbackLabel) {
      const translatedFallback = this.translate.instant(staticFallbackLabel);
      if (translatedFallback !== staticFallbackLabel) {
        return translatedFallback;
      }
    }

    return rawValue;
  }
}
